import type { Translations } from "../../i18n/en";
import type { MarketMood, PricingAction } from "../../services/outlookApi";
import type { PmsSyncStatusOn, SyncStatusTonight } from "../../services/syncStatusApi";
import { OUTLOOK_STALE_AFTER_MS, isOutlookStale, type OutlookTone } from "../dashboard/outlookCopy";

/**
 * components/pms/tonightReading.ts
 *
 * What the sync indicator says of tonight: the rail's third line, the folded
 * rail's tooltip and the popover's "Today" section. Every rule is here, so
 * `PmsSyncStatus` only draws.
 *
 * The dashboard says what Optivo did with tonight's prices and how tonight's
 * market is, and the rail is on every page where the dashboard is one of
 * them. So the same reading rides on the status the rail already polls
 * (`tonight` on `GET /api/sync-status`), and the indicator gains one quiet
 * line for it. No block of its own: the rail was asked not to get busy.
 *
 * Two things decide what is said.
 *
 * **The rail says it only where it can prove it**, the indicator's own rule.
 * "Discounting today" under a property's name is a claim about what a guest
 * is being quoted. It holds only while the sync is healthy, the prices leave
 * for the PMS on their own, and the engine calculated them within the day.
 * Anything short of that and the line is not drawn (`tonightLine`). The
 * popover has the room to be exact, so it says what it has whatever the
 * verdict, each caveat in a sentence of its own (`tonightSentences`).
 *
 * **Every value is read as a word this page has, or as nothing.** The reading
 * is the server's, the page and the service are released apart and a tab
 * stays open for days, so a pricing action or a mood the server gains is met
 * by pages built before it. The first value the indicator had no words for
 * took down every page the rail is on (an issue kind, 4 Oct 2026). Here such a
 * value costs its word. On the rail there is then no line. In the popover it
 * is worded as the dashboard words it, as no reading on the hollow mark: one
 * night is not "no data" on one screen and a blank on the other. Never an
 * empty row, the text "undefined", a tone the page made up or a throw. That
 * holds for a reading that is no reading at all, and for a bundle that lacks
 * the words, which is what the test harnesses hand components.
 */

/**
 * Past this, tonight's prices are older than the slowest sync leaves them (a
 * day), with two hours for a run that started late. The dashboard's own bound
 * and its own rule (`isOutlookStale` in `dashboard/outlookCopy.ts`), so the
 * rail and the dashboard's nights cannot call one calculation fresh and stale
 * at once.
 */
export const TONIGHT_STALE_AFTER_MS = OUTLOOK_STALE_AFTER_MS;

/** The words the rail's line is said in: `syncStatus.tonight`. */
type TonightWords = Translations["syncStatus"]["tonight"];

/**
 * The actions whose prices are not recalculated by design: every room of the
 * night is held by a lock, or none is left to sell. Their word is true however
 * old the last calculation is, so it is printed where another action's would
 * give way to "Prices over a day old".
 */
const NOT_RECALCULATED: ReadonlySet<unknown> = new Set<PricingAction>(["locked", "nothingToSell"]);

/**
 * The days the rail names the market: the ones it is not ordinary. A
 * favourable or a calm market is most days, and a word for it on every page
 * would be the busy rail this line was asked not to be. The rule is this set,
 * not which words a bundle happens to carry.
 */
const MARKET_WORTH_A_WORD: ReadonlySet<unknown> = new Set<MarketMood>(["strong", "soft", "weak"]);

/**
 * The tone each mood this page lists is drawn in, the dashboard's own
 * (`marketTone` in `dashboard/outlookCopy.ts`; `tonightReading.test.ts` holds
 * the two together). A table of its own because that function answers for any
 * value, and answered "good" for every one it did not know: a mood from a
 * newer server would have been drawn as a green dot. Read as a tone or as
 * nothing, it also says which moods the page lists.
 */
const MARKET_TONE: Record<MarketMood, OutlookTone> = {
    strong: "good",
    favourable: "good",
    calm: "good",
    soft: "caution",
    weak: "bad",
};

/** The moods under which the weather and the nearby rates are worded as lifting the market. */
const LIFTING: ReadonlySet<unknown> = new Set<MarketMood>(["strong", "favourable"]);

/** How many drivers are named under a market: the server sends the two strongest. */
const MAX_DRIVERS = 2;

/**
 * The table's word for a value, or null where it has none.
 *
 * Read as a string or as nothing, as `issueWords` in `PmsSyncStatus` reads an
 * issue kind, and never with `??`: a value called `constructor` reads back
 * `Object`. Nothing is assumed of either side. The table may be missing from a
 * partial bundle, and the value is off the wire, where a field can hold
 * anything.
 */
function wordFor(table: unknown, value: unknown): string | null {
    if (typeof value !== "string" || value === "") return null;
    if (table === null || typeof table !== "object") return null;
    const word: unknown = (table as Record<string, unknown>)[value];
    return typeof word === "string" && word !== "" ? word : null;
}

/**
 * Whether a table has a sentence for a value off the wire. `unknown` is the
 * table's own key for no reading, never a value the server sends: off the
 * wire it is one more word this page does not list.
 */
function isListed(table: unknown, value: unknown): boolean {
    return value !== "unknown" && wordFor(table, value) !== null;
}

/** The table's sentence for no reading: the server's null, or a value this page does not list. */
function noReading(table: unknown): string | null {
    return wordFor(table, "unknown");
}

/** The outlook's strings, or nothing where the bundle has none. */
function outlookOf(t: Translations): unknown {
    const dashboard: unknown = (t as Partial<Translations>).dashboard;
    return dashboard !== null && typeof dashboard === "object"
        ? (dashboard as Partial<Translations["dashboard"]>).outlook
        : undefined;
}

/** A named part of a group of strings, or nothing where the group is missing. */
function partOf(group: unknown, key: string): unknown {
    return group !== null && typeof group === "object" ? (group as Record<string, unknown>)[key] : undefined;
}

/**
 * The status's `tonight` as a reading, or null where it carries none.
 *
 * Only an object is a reading. The field is absent from a server that
 * predates it and from one that could not compose the reading, and what is
 * inside an object is still read value by value.
 */
export function readingOf(tonight: unknown): SyncStatusTonight | null {
    return tonight !== null && typeof tonight === "object" ? (tonight as SyncStatusTonight) : null;
}

/** Whether the reading says when the prices were calculated. */
function isDated(tonight: SyncStatusTonight): boolean {
    const stamp: unknown = tonight.calculatedAt;
    return typeof stamp === "string" && Number.isFinite(Date.parse(stamp));
}

/**
 * The rail's third line: what Optivo did with tonight's prices and, on a day
 * it is not ordinary, the market. Or that the prices are over a day old.
 */
export type TonightLine =
    | { pricing: string; market: string | null }
    | { stale: string };

/**
 * The indicator's third line, or null where it draws none.
 *
 * Drawn only while all of this holds:
 *
 * - The status carries a reading.
 * - The verdict is `good` or `syncing`. Under a fault the line above already
 *   says what matters, and a claim about prices under "Last run failed" is one
 *   nobody can vouch for. `syncing` keeps the line: a run is in flight every
 *   half hour, and a line that left with each would blink all day. A verdict
 *   the page does not list is not `good`, as everywhere else in the indicator.
 * - Calculated prices leave for the PMS on their own: the auto-sync is on and
 *   its schedule sends prices. With manual sending the plan reaches no guest
 *   until somebody sends it, and "Discounting today" would describe a price
 *   nobody is quoted. The popover says that in a sentence instead.
 * - The page has a word for the pricing action. Null (nothing can be claimed)
 *   and a value it does not list both leave the line out.
 * - The reading says when the prices were calculated. Older than
 *   `TONIGHT_STALE_AFTER_MS`, the line says that in the word's place, since a
 *   stopped engine would otherwise wear yesterday's word over today. A night
 *   held by locks or sold out prints its word whatever the age: nothing
 *   recalculates those by design.
 *
 * The market is added for a strong, a soft and a weak one only, and never
 * over old prices.
 */
export function tonightLine(status: PmsSyncStatusOn, words: TonightWords | undefined, now: Date): TonightLine | null {
    const tonight = readingOf(status.tonight);
    if (!tonight) return null;

    const verdict: unknown = status.verdict;
    if (verdict !== "good" && verdict !== "syncing") return null;

    const push: Partial<PmsSyncStatusOn["push"]> | undefined = status.push;
    if (push?.autoSyncEnabled !== true || tonight.autoSendsPrices !== true) return null;

    const pricing = wordFor(partOf(words, "pricing"), tonight.pricing);
    if (!pricing) return null;

    if (!isDated(tonight)) return null;
    if (isOutlookStale(tonight.calculatedAt, now) && !NOT_RECALCULATED.has(tonight.pricing)) {
        const stale = wordFor(words, "stale");
        return stale ? { stale } : null;
    }

    const market = MARKET_WORTH_A_WORD.has(tonight.market) ? wordFor(partOf(words, "market"), tonight.market) : null;
    return { pricing, market };
}

/** What the popover's "Today" section says, each part null where it has nothing to say. */
export interface TonightSentences {
    /** What Optivo did with the prices, and the action its mark is drawn for; null draws the hollow mark. */
    pricing: { action: PricingAction | null; sentence: string } | null;
    /** The market, the tone of its mark and what is behind it. `mood` is null where the page reads no market. */
    market: { mood: MarketMood | null; tone: OutlookTone; sentence: string; drivers: string[] } | null;
    /** The caution over prices more than a day old. */
    stale: string | null;
    /** That the prices do not leave for the PMS on their own. */
    manual: string | null;
}

/**
 * One driver in words, as `describeMarketDrivers` in `dashboard/outlookCopy.ts`
 * words it (`tonightReading.test.ts` holds the two together), or null where
 * the page cannot word it: a kind it does not list, or what is no driver at
 * all. Labels that are no list of names read as none.
 */
function describeDriver(t: Translations, outlook: unknown, driver: unknown, lifting: boolean): string | null {
    if (driver === null || typeof driver !== "object") return null;
    const { kind, labels: rawLabels } = driver as { kind?: unknown; labels?: unknown };
    const labels = Array.isArray(rawLabels)
        ? rawLabels.filter((label): label is string => typeof label === "string" && label !== "")
        : [];
    const names = partOf(outlook, "drivers");

    switch (kind) {
        case "schoolHoliday":
        case "specialDate":
            return labels.length > 0 ? labels.join(", ") : wordFor(names, kind);
        case "weather":
            return wordFor(names, lifting ? "weatherUp" : "weatherDown");
        case "weatherExtreme": {
            // Named as the Market Conditions page names it. A kind of extreme
            // the feed gains before it has a name there is still extreme weather.
            const marketConditions: unknown = (t as Partial<Translations>).marketConditions;
            const kinds = partOf(partOf(marketConditions, "weatherExtremes"), "kinds");
            const named = labels.map((label) => wordFor(kinds, label)).filter((name): name is string => name !== null);
            return named.length > 0 ? named.join(", ") : wordFor(names, "weatherExtreme");
        }
        case "competitorRates":
            return wordFor(names, lifting ? "competitorRatesUp" : "competitorRatesDown");
        default:
            return null;
    }
}

/**
 * Tonight in the dashboard's own sentences, so one night is never worded two
 * ways: its sentence for the prices and for the market
 * (`dashboard.outlook.pricingStatus`, `marketStatus`) and its caution over old
 * prices (`staleCalculation`, which the nights' heading shows).
 *
 * Where the server claims nothing (null), and where it sends a value this page
 * does not list, the sentence is the table's own `unknown` on the hollow mark,
 * as the dashboard words both (`listed` in `dashboard/outlookCopy.ts`). An
 * unlisted mood is in particular never handed to a tone: it would have been
 * drawn as good news.
 *
 * The drivers are named under a mood the page lists, the two strongest it can
 * word. Which way the weather and the nearby rates push is read off the mood,
 * so there is nothing to say of them under no mood.
 *
 * `manual` is the indicator's own sentence (`syncStatus.tonight.manual`): the
 * dashboard's speaks of "the outlook", which the popover has none of. It is
 * said only on the server's own no: a reading that does not state whether
 * prices leave is not one that says they do not.
 */
export function tonightSentences(tonight: SyncStatusTonight, t: Translations, now: Date = new Date()): TonightSentences {
    const reading = readingOf(tonight);
    const outlook = outlookOf(t);
    if (!reading) return { pricing: null, market: null, stale: null, manual: null };

    const pricingTable = partOf(outlook, "pricingStatus");
    const pricingValue: unknown = reading.pricing;
    const action = isListed(pricingTable, pricingValue) ? (pricingValue as PricingAction) : null;
    const pricingSentence = action ? wordFor(pricingTable, action) : noReading(pricingTable);

    // Which moods the page lists is the tone table's to say: a mood with a
    // sentence in some bundle and no tone here is not one of them.
    const marketTable = partOf(outlook, "marketStatus");
    const moodValue: unknown = reading.market;
    const tone = wordFor(MARKET_TONE, moodValue);
    const mood = tone !== null && isListed(marketTable, moodValue) ? (moodValue as MarketMood) : null;
    const marketSentence = mood ? wordFor(marketTable, mood) : noReading(marketTable);
    const lifting = LIFTING.has(mood);
    const rawDrivers: unknown = reading.marketDrivers;
    const drivers = mood !== null && Array.isArray(rawDrivers)
        ? rawDrivers
            .map((driver) => describeDriver(t, outlook, driver, lifting))
            .filter((name): name is string => name !== null)
            .slice(0, MAX_DRIVERS)
        : [];

    return {
        pricing: pricingSentence ? { action, sentence: pricingSentence } : null,
        market: marketSentence
            ? { mood, tone: mood ? (tone as OutlookTone) : "unknown", sentence: marketSentence, drivers }
            : null,
        stale: isDated(reading) && isOutlookStale(reading.calculatedAt, now) ? wordFor(outlook, "staleCalculation") : null,
        manual: reading.autoSendsPrices === false ? wordFor(wordsOf(t), "manual") : null,
    };
}

/** The words the rail's line is said in, or nothing where the bundle has none. */
function wordsOf(t: Translations): TonightWords | undefined {
    return partOf(partOf(t, "syncStatus"), "tonight") as TonightWords | undefined;
}

/** What heads the popover's section, or null in a bundle without the word. */
export function tonightTitle(t: Translations): string | null {
    return wordFor(wordsOf(t), "title");
}

/**
 * What the folded rail's tooltip adds under the state and its time.
 *
 * The folded rail has no room for a third line, so its tooltip carries the
 * sentences. What Optivo did with the prices is said where the open rail's
 * line would be drawn, by the same rule (`tonightLine`), and where that line
 * would say the prices are old, so does this. The market is said on every day
 * the page can name it, a calm one too: a tooltip is asked for, where the
 * rail's line is on every page unasked. It claims nothing of what a guest is
 * quoted, so it does not wait on the sync or on how prices are sent.
 */
export function tonightTooltipLines(status: PmsSyncStatusOn, t: Translations, now: Date = new Date()): string[] {
    const tonight = readingOf(status.tonight);
    if (!tonight) return [];

    const sentences = tonightSentences(tonight, t, now);
    const line = tonightLine(status, wordsOf(t), now);
    const lines: string[] = [];

    if (line) {
        const said = "stale" in line ? sentences.stale : sentences.pricing?.sentence;
        if (said) lines.push(said);
    }
    if (sentences.market && sentences.market.mood !== null) lines.push(sentences.market.sentence);

    return lines;
}
